import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { LoginActions } from '../../Services/_actions/LoginAction/LoginAction.ts';
import ReCAPTCHA from 'react-google-recaptcha';
import Grid from '@mui/material/Grid';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import LoadingButton from '../Shared/Loading/index.tsx';
import ForgotPasswordModal from './ForgotPasswordModal.tsx';
import GuestLoginModal from './GuestLoginModal.tsx';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { authAtom, userInformationAtom } from '../../Services/_states/auth.js';
import { useLocation, useNavigate } from 'react-router';
import { ROUTES } from '../../constants/routes';
import { AUTHZ_FEATURES } from '../../constants/authz';
import { resolveAuthzV2Enabled } from '../../config/authz';
import { isSubdomainLoginAllowed } from '../../utils/isSubdomainLoginAllowed';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, Chip, Alert } from '@mui/material';
import MachadaloLogo from '../Header/machadalo-logo.png';
import TestLogo from '../Header/synergytop-logo.webp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LockIcon from '@mui/icons-material/Lock';
import { useSearchParams } from 'react-router-dom';

const Logo =
  process.env.REACT_APP_SUBDOMAIN_POSTFIX === '.synergytop.com' ? TestLogo : MachadaloLogo;

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(3),
  gap: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

export default function SignInCard() {
  const action = LoginActions();
  const [credential, setCredential] = React.useState({ username: '', password: '' });
  const [captchaToken, setCaptchaToken] = React.useState(null);
  const [loginError, setLoginError] = React.useState('');
  const [isLoginInProgress, setIsLoginInProgress] = React.useState(false);
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = React.useState(false);
  const [guestModalOpen, setGuestModalOpen] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  // ── MFA state ──────────────────────────────────────────────
  const [mfaStep, setMfaStep] = React.useState(false); // true = show OTP screen
  const [pendingUserId, setPendingUserId] = React.useState<number | null>(null);
  const [otp, setOtp] = React.useState('');
  const [otpError, setOtpError] = React.useState('');
  const [isOtpVerifying, setIsOtpVerifying] = React.useState(false);
  const [otpTimeLeft, setOtpTimeLeft] = React.useState(300);
  const [otpExpired, setOtpExpired] = React.useState(false);
  const [isResending, setIsResending] = React.useState(false);
  const [otpResendTrigger, setOtpResendTrigger] = React.useState(0);
  // ───────────────────────────────────────────────────────────

  const setUserInfo = useSetRecoilState(userInformationAtom);
  const { showSnackBar } = useSnackBar();
  const setAuth = useSetRecoilState(authAtom);
  const auth = useRecoilValue(authAtom);
  const navigate = useNavigate();
  const Location = useLocation();

  const [searchParams] = useSearchParams();
  const usernameParam = searchParams.get('username');
  const passwordParam = searchParams.get('password');
  const [email, setEmail] = React.useState("");
  const recaptchaRef = React.useRef(null);
  const otpIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const stopOtpTimer = () => {
    if (otpIntervalRef.current) {
      clearInterval(otpIntervalRef.current);
      otpIntervalRef.current = null;
    }
  };

  React.useEffect(() => {
    if (usernameParam || passwordParam) {
      setCredential({
        username: usernameParam ?? '',
        password: passwordParam ?? '',
      });
    }
  }, [usernameParam, passwordParam]);

  // ── OTP countdown timer ───────────────────────────────────
  React.useEffect(() => {
    if (!mfaStep) return;

    setOtpTimeLeft(300);
    setOtpExpired(false);

    otpIntervalRef.current = setInterval(() => {
      setOtpTimeLeft((prev) => {
        if (prev <= 1) {
          stopOtpTimer();
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return stopOtpTimer;
  }, [mfaStep, otpResendTrigger]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ── Navigation helper ──────────────────────────────────────
  // const navigateToDashboard = (userData: any = {}) => {
  //   if (resolveAuthzV2Enabled(userData)) {
  //     const features = userData?.profile?.rbac_grants?.features || {};
  //     if (features[AUTHZ_FEATURES.INTERVENE.DASHBOARD]) {
  //       return navigate(ROUTES.FEATURES.INTERVENE);
  //     }
  //     if (features[AUTHZ_FEATURES.WHATSAPP.SEND_TEMPLATE]) {
  //       return navigate(ROUTES.FEATURES.WHATSAPP_TEMPLATES);
  //     }
  //     if (features[AUTHZ_FEATURES.FLOW.PUBLISH]) {
  //       return navigate(ROUTES.FEATURES.FLOW_LIST);
  //     }
  //     return navigate(ROUTES.DASHBOARD.HUB);
  //   }

  //   const userPermissions = userData?.profile?.permissions || {};
  //   if (userPermissions?.campaign_leads_page_access) return navigate(ROUTES.LEGACY.RECORDS);
  //   if (userPermissions?.homepage_access) return navigate('/r/management/manageCampaign');
  //   if (userPermissions?.can_view_template_dashboard_new || userPermissions?.whatsapp_template_access) {
  //     return navigate(ROUTES.FEATURES.WHATSAPP_TEMPLATES);
  //   }
  //   if (userPermissions?.can_view_intervene_new) return navigate(ROUTES.FEATURES.INTERVENE);
  //   if (userPermissions?.can_view_flow_builder) return navigate(ROUTES.FEATURES.FLOW_LIST);
  //   if (userPermissions?.can_view_web_form) return navigate(ROUTES.FEATURES.LEAD_WEB_FORM);
  //   if (userPermissions?.can_view_ecommerce_dashboard) return navigate(ROUTES.LEGACY.ECOMMERCE_ORDERS);
  //   if (userPermissions?.can_view_concept_builder) return navigate('/r/student/concept-builder');
  //   return navigate(ROUTES.DASHBOARD.HUB);
  // };
  const navigateToDashboard = (userData: any = {}) => {
    return navigate(ROUTES.DASHBOARD.HUB);
  };

  React.useEffect(() => {
    if (auth?.token && Location.pathname === '/r/login') {
      try {
        const userData = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (userData?.profile) {
          navigateToDashboard(userData);
        }
      } catch {
        navigate('/r/leads/v1');
      }
    }
  }, [auth, navigate]);

  // ── Fetch user info & finalise login ──────────────────────
  // const fetchUserInfo = async (authResponse: any) => {
  //   try {
  //     const userInfoResponse = await action.userInformation(authResponse?.user_id);
  //     if (userInfoResponse?.status === 200 && userInfoResponse?.data) {
  //       const userData = userInfoResponse.data;
  //       const subDomain = userData?.profile?.organisation?.sub_domain;
  //       const postfix = process.env.REACT_APP_SUBDOMAIN_POSTFIX ?? '.machadalo.com';
  //       const newDomain = `${subDomain}${postfix}`;

  //       if (!subDomain && window?.location?.origin === process.env.REACT_APP_ORIGIN) {
  //         // same origin, no subdomain – ok
  //       } else if (subDomain && window.location.host === newDomain) {
  //         // correct subdomain – ok
  //       } else {
  //         localStorage.clear();
  //         setAuth(null);
  //         setLoginError('Invalid Credentials');
  //         return;
  //       }

  //       setAuth({
  //         token: authResponse?.token,
  //         isAuthenticated: true,
  //         user: authResponse?.user,
  //       });

  //       if (userData.profile) {
  //         const permissions: Record<string, boolean> = {};
  //         (userData.profile.general_user_permission || []).forEach((perm: any) => {
  //           permissions[perm.name.toLowerCase()] = perm.is_allowed;
  //         });
  //         userData.profile.permissions = permissions;
  //       }

  //       localStorage.setItem('userInfo', JSON.stringify(userData));
  //       setUserInfo(userData);
  //       navigateToDashboard(userData?.profile?.permissions);
  //     }
  //   } catch (error) {
  //     console.error('Failed to fetch user info:', error);
  //   }
  // };

  const fetchUserInfo = async (authResponse: any) => {
    try {
      const userInfoResponse = await action.userInformation(authResponse?.user_id);
      if (userInfoResponse?.status === 200 && userInfoResponse?.data) {
        const userData = userInfoResponse.data;
        const subDomain = userData?.profile?.organisation?.sub_domain;

        if (!isSubdomainLoginAllowed(subDomain)) {
          stopOtpTimer();
          localStorage.clear();
          setAuth(null);
          const msg = 'Invalid Credentials';
          setLoginError(msg);
          setOtpError(msg);
          setMfaStep(false);
          return;
        }

        stopOtpTimer();
        setAuth({ token: authResponse?.token, isAuthenticated: true, user: authResponse?.user });

        if (userData.profile) {
          const permissions: Record<string, boolean> = {};
          (userData.profile.general_user_permission || []).forEach((perm: any) => {
            if (perm?.name) {
              permissions[perm.name.toLowerCase()] = perm.is_allowed;
            }
          });

          if (resolveAuthzV2Enabled(userData)) {
            const features = userData.profile.rbac_grants?.features || {};
            Object.entries(features).forEach(([key, value]) => {
              permissions[key.toLowerCase()] = value as boolean;
            });
          }

          userData.profile.permissions = permissions;
        }
        localStorage.setItem('userInfo', JSON.stringify(userData));
        setUserInfo(userData);
        navigateToDashboard(userData);
      } else {
        stopOtpTimer();
        setLoginError('Unable to complete sign in. Please try logging in again.');
        setOtpError('Unable to complete sign in. Please try logging in again.');
        setMfaStep(false);
      }
    } catch (error) {
      stopOtpTimer();
      console.error('Failed to fetch user info:', error);
      const msg = 'Something went wrong after verification. Please try logging in again.';
      setLoginError(msg);
      setOtpError(msg);
      setMfaStep(false);
    }
  };


  // ── Step 1 : Username + Password submit ───────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!credential.username && !credential.password) {
      return setLoginError('Username and Password are required.');
    }
    if (!credential.username) return setLoginError('Username is required.');
    if (!credential.password) return setLoginError('Password is required.');
    if (!captchaToken) return setLoginError('Please complete the reCAPTCHA.');

    setIsLoginInProgress(true);
    try {
      const encryptedResponse = await action.login(credential);

      // Check if response status is successful
      if (encryptedResponse?.status === 200 && encryptedResponse?.data) {
        const response = encryptedResponse.data;
        localStorage.setItem('machadalo-credentials', JSON.stringify(response));

        // ── Check if backend requires MFA (returns user_id but no token yet) ──
        if (response?.user_id && !response?.token) {
          // MFA required – move to OTP step
          setPendingUserId(response.user_id);
          setEmail(response.email ?? '');
          setMfaStep(true);
        } else {
          // No MFA – proceed directly
          await fetchUserInfo(response);
        }
      } else {
        // ── Handle error cases from response ──
        const errorData = encryptedResponse?.data || encryptedResponse || {};

        if (errorData?.non_field_errors && Array.isArray(errorData.non_field_errors) && errorData.non_field_errors.length > 0) {
          setLoginError(errorData.non_field_errors[0]);
        }
        else if (errorData?.detail) {
          setLoginError(errorData.detail);
        }
        else if (errorData?.error) {
          setLoginError(errorData.error);
        }
        else if (errorData?.message) {
          setLoginError(errorData.message);
        }
        else if (errorData?.username && Array.isArray(errorData.username)) {
          setLoginError(errorData.username[0]);
        }
        else if (errorData?.password && Array.isArray(errorData.password)) {
          setLoginError(errorData.password[0]);
        }
        else {
          setLoginError('Invalid credentials. Please try again.');
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);

      // ── Handle axios error properly ──
      // Check if it's an axios error with response
      if (error?.response?.data) {
        const errorData = error.response.data;

        // Check for non_field_errors (most common for your API)
        if (errorData?.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          setLoginError(errorData.non_field_errors[0]);
        }
        // Check for detail
        else if (errorData?.detail) {
          setLoginError(errorData.detail);
        }
        // Check for error
        else if (errorData?.error) {
          setLoginError(errorData.error);
        }
        // Check for message
        else if (errorData?.message) {
          setLoginError(errorData.message);
        }
        // Check for field errors
        else if (errorData?.username && Array.isArray(errorData.username)) {
          setLoginError(errorData.username[0]);
        }
        else if (errorData?.password && Array.isArray(errorData.password)) {
          setLoginError(errorData.password[0]);
        }
        // If it's a string error
        else if (typeof errorData === 'string') {
          setLoginError(errorData);
        }
        else {
          setLoginError('Login failed. Please try again.');
        }
      }
      // Check if error has a message
      else if (error?.message) {
        setLoginError(error.message);
      }
      else {
        setLoginError('Login failed. Please try again.');
      }
    } finally {
      setIsLoginInProgress(false);
    }
  };

  const maskEmail = (email: string) => {
    if (!email || !email.includes('@')) return '';
    const [local, domain] = email.split('@');
    const [domainName, ...rest] = domain.split('.');
    const tld = rest.join('.'); // handles domains like co.in too

    const maskedLocal =
      local.length <= 4
        ? local[0] + '*'.repeat(Math.max(local.length - 1, 1))
        : local.slice(0, 2) + '*'.repeat(local.length - 4) + local.slice(-2);

    const maskedDomain = '*'.repeat(domainName.length);

    return `${maskedLocal}@${maskedDomain}.${tld}`;
  };

  // ── Step 2 : OTP verification ─────────────────────────────
  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');

    if (otpExpired) {
      return setOtpError('OTP has expired. Please go back and login again.');
    }
    if (!otp.trim()) return setOtpError('Please enter the OTP from your authenticator app.');
    if (!/^\d{6}$/.test(otp.trim())) return setOtpError('OTP must be a 6-digit number.');

    setIsOtpVerifying(true);
    try {
      const response = await action.otpLogin({
        user_id: pendingUserId,
        otp: otp.trim(),
      });

      if (response?.status === 200 && response?.data) {
        const authData = response.data;
        localStorage.setItem('machadalo-credentials', JSON.stringify(authData));
        await fetchUserInfo(authData);
      } else {
        const apiError = response?.data;
        if (apiError?.non_field_errors?.length) {
          setOtpError(apiError.non_field_errors[0]);
        } else if (apiError?.detail) {
          setOtpError(apiError.detail);
        } else {
          setOtpError('Invalid OTP. Please try again.');
        }
      }
    } catch {
      setOtpError('OTP verification failed. Please try again.');
    } finally {
      setIsOtpVerifying(false);
    }
  };

  // ── Resend OTP ─────────────────────────────────────────────
  const handleResendOtp = async () => {
    setOtpError('');
    setIsResending(true);
    try {
      const response = await action.login(credential);

      if (response?.status === 200 && response?.data) {
        const data = response.data;
        if (data?.user_id) {
          setPendingUserId(data.user_id);
          setEmail(data.email ?? email);
        }
        setOtp('');
        setOtpResendTrigger((prev) => prev + 1);
        showSnackBar?.('A new OTP has been sent.', 'success');
      } else {
        setOtpError('Failed to resend OTP. Please try again.');
      }
    } catch {
      setOtpError('Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaStep(false);
    setPendingUserId(null);
    setOtp('');
    setOtpError('');
    setLoginError('');
    setOtpTimeLeft(300);
    setOtpExpired(false);
    setCaptchaToken(null);
    recaptchaRef.current?.reset();
  };

  // ── Shared error box ──────────────────────────────────────
  const ErrorBox = ({ message }: { message: string }) =>
    message ? (
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'error.lighter',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'error.light',
        }}
      >
        <Typography color="error" variant="body2">
          {message}
        </Typography>
      </Box>
    ) : null;

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <>
      <Card variant="outlined">
        {/* ── Mobile logo ─────────────────────────────────── */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
          }}
        >
          <img src={Logo} alt="Logo" style={{ maxWidth: 100 }} />
          <Chip
            icon={<PsychologyIcon />}
            label="DeTAiL - AI Powered Learning"
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>

        {/* ══════════════════════════════════════════════════
            STEP 1 — Username / Password
        ══════════════════════════════════════════════════ */}
        {!mfaStep && (
          <>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{ fontSize: 'clamp(1.75rem, 10vw, 2.15rem)', fontWeight: 700, mb: 0.5 }}
              >
                Sign in
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', display: { xs: 'block', md: 'none' } }}
              >
                Access your personalised learning platform
              </Typography>
            </Box>

            <ErrorBox message={loginError} />

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
            >
              <FormControl>
                <FormLabel htmlFor="username">Username</FormLabel>
                <TextField
                  id="username"
                  placeholder="Enter your username"
                  type="text"
                  name="username"
                  size="small"
                  autoComplete="username"
                  value={credential.username}
                  onChange={(e) => setCredential({ ...credential, username: e.target.value })}
                  required
                  fullWidth
                  error={!!loginError && !credential.username}
                  helperText={!!loginError && !credential.username ? 'Username is required' : ''}
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="password">Password</FormLabel>
                <TextField
                  id="password"
                  placeholder="Enter your password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  size="small"
                  autoComplete="current-password"
                  value={credential.password}
                  onChange={(e) => setCredential({ ...credential, password: e.target.value })}
                  required
                  fullWidth
                  error={!!loginError && !credential.password}
                  helperText={!!loginError && !credential.password ? 'Password is required' : ''}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <Box mt={2} display="flex" justifyContent="center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                  size="normal"
                />
              </Box>

              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                buttonName="Sign in"
                isLoading={isLoginInProgress}
                isDisabled={!captchaToken}
              />

              <Grid container justifyContent="center">
                <Grid item>
                  <Button size="small" onClick={() => setForgotPasswordModalOpen(true)}>
                    Forgot Password?
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 1, display: { xs: 'block', md: 'none' } }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Powered by Deep AI
              </Typography>
            </Divider>
          </>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 2 — MFA / OTP verification
        ══════════════════════════════════════════════════ */}
        {mfaStep && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: 'primary.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LockIcon color="primary" fontSize="large" />
              </Box>
              <Typography component="h1" variant="h5" fontWeight={700} textAlign="center">
                Two-Factor Authentication
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Open your authenticator app and enter the 6-digit verification code.
              </Typography>
            </Box>

            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              {email && (
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  Code sent to <strong>{maskEmail(email)}</strong>
                </Typography>
              )}
            </Alert>

            <ErrorBox message={otpError} />

            <Box
              component="form"
              onSubmit={handleOtpVerify}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <FormControl>
                <FormLabel htmlFor="otp">Verification Code</FormLabel>
                <TextField
                  id="otp"
                  placeholder="Enter 6-digit code"
                  type="text"
                  inputMode="numeric"
                  size="small"
                  autoFocus
                  value={otp}
                  disabled={otpExpired}
                  onChange={(e) => {
                    // Allow only digits, max 6
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(val);
                  }}
                  required
                  fullWidth
                  error={!!otpError}
                  inputProps={{
                    maxLength: 6,
                    style: {
                      letterSpacing: '0.5em',
                      fontSize: '1.4rem',
                      textAlign: 'center',
                      fontWeight: 600,
                    },
                  }}
                />
              </FormControl>
              {/* ── Countdown Timer ── */}
              {/* <Typography
                variant="caption"
                sx={{
                  fontWeight: 900,
                  fontSize: '0.85rem',
                  color: otpExpired
                    ? 'error.main'
                    : otpTimeLeft <= 30
                      ? 'warning.main'
                      : 'black',
                }}
              >
                {otpExpired
                  ? 'Code expired'
                  : `Code expires in ${formatTime(otpTimeLeft)}`}
              </Typography> */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: otpExpired
                      ? 'error.main'
                      : otpTimeLeft <= 30
                        ? 'warning.main'
                        : 'text.secondary',
                  }}
                >
                  {otpExpired
                    ? 'Code expired'
                    : `Code expires in ${formatTime(otpTimeLeft)}`}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary' }}
                >
                  Didn't receive the code?{' '}
                  <Button
                    variant="text"
                    size="small"
                    disableRipple
                    onClick={handleResendOtp}
                    // disabled={!otpExpired || isResending}
                    sx={{
                      minWidth: 'auto',
                      p: 0,
                      ml: 0.5,
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    {isResending ? 'Sending...' : 'Resend'}
                  </Button>
                </Typography>
              </Box>

              {/* <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                buttonName="Verify & Sign in"
                isLoading={isOtpVerifying}
              /> */}

              {!otpExpired ? (
                <LoadingButton
                  variant="contained"
                  size="small"
                  type="submit"
                  buttonName="Verify & Sign in"
                  isLoading={isOtpVerifying}
                  isDisabled={otp.trim().length !== 6}
                />
              ) : (
                <LoadingButton
                  variant="contained"
                  size="small"
                  type="button"
                  buttonName="Resend OTP"
                  isLoading={isResending}
                  onClickButton={handleResendOtp}
                />
              )}

              <Button
                variant="text"
                size="small"
                onClick={handleBackToLogin}
                sx={{ alignSelf: 'center' }}
              >
                ← Back to login
              </Button>
            </Box>
          </>
        )}
      </Card>

      <GuestLoginModal open={guestModalOpen} onClose={() => setGuestModalOpen(false)} />
      <ForgotPasswordModal
        open={forgotPasswordModalOpen}
        onClose={() => setForgotPasswordModalOpen(false)}
      />
    </>
  );
}